"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type React from "react";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type TableVariant = "default" | "card";

export type TableLayout = "auto" | "fixed";

/** Which edge of its cell a column's content sits against. */
export type TableAlign = "start" | "end";

const ALIGN_CLASS: Record<TableAlign, string> = {
  start: "text-left",
  end: "text-right",
};

/**
 * A px column width is an inline `style`, never a class or the deprecated
 * presentational attribute: the value is arbitrary, so no utility can carry it.
 * A caller's own `style` keeps every key the width does not name.
 */
function widthStyle(
  width: number | undefined,
  style: React.CSSProperties | undefined,
): React.CSSProperties | undefined {
  if (width === undefined) return style;
  return { ...style, width };
}

export type TableProps = React.ComponentProps<"table"> & Omittable<{
  variant?: TableVariant;
  /**
   * `"fixed"` is what makes a declared column width hold: the browser sizes
   * every column from the first row alone, so a `width` on one `TableHead`
   * stands and the unsized columns share the remainder. It is also what bounds
   * a cell — under `"auto"` the widest content sets the column, so a long value
   * widens the table instead of letting `Text truncate` ellipsize inside it.
   * Declare a width on any column and fix the layout in the same breath.
   */
  layout?: TableLayout;
  render?: useRender.ComponentProps<"div">["render"];
}>;

export function Table({
  className,
  variant = "default",
  layout = "auto",
  render,
  ...props
}: TableProps): React.ReactElement {
  const defaultProps = {
    children: (
      <table
        className={cn(
          "w-full caption-bottom in-data-[variant=card]:border-separate in-data-[variant=card]:border-spacing-0 text-sm",
          layout === "fixed" && "table-fixed",
          className,
        )}
        data-slot="table"
        {...props}
      />
    ),
    // The base Table ships `overflow-x-auto` here; removed — a table's
    // scrolling is owned by its ancestor surface (the DataTable particle's
    // scroll Box), and a self-scrolling container breaks sticky headers and
    // virtualizer scroll ownership. Wrap in a scrolling Box where needed.
    className: "relative w-full",
    "data-slot": "table-container",
    "data-variant": variant,
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, {}),
    render,
  });
}

export function TableHeader({
  className,
  ...props
}: React.ComponentProps<"thead">): React.ReactElement {
  return (
    <thead
      className={cn("[&_tr]:border-b", className)}
      data-slot="table-header"
      {...props}
    />
  );
}

export function TableBody({
  className,
  ...props
}: React.ComponentProps<"tbody">): React.ReactElement {
  return (
    <tbody
      className={cn(
        "relative in-data-[variant=card]:rounded-xl in-data-[variant=card]:shadow-xs/5 before:pointer-events-none before:absolute before:inset-px not-in-data-[variant=card]:before:hidden before:rounded-[calc(var(--radius-xl)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/8%)] [&_tr:last-child]:border-0 in-data-[variant=card]:*:[tr]:border-0 in-data-[variant=card]:*:[tr]:*:[td]:border-b in-data-[variant=card]:*:[tr]:*:[td]:bg-card in-data-[variant=card]:*:[tr]:first:*:[td]:first:rounded-ss-xl in-data-[variant=card]:*:[tr]:*:[td]:first:border-s in-data-[variant=card]:*:[tr]:first:*:[td]:border-t in-data-[variant=card]:*:[tr]:last:*:[td]:last:rounded-ee-xl in-data-[variant=card]:*:[tr]:*:[td]:last:border-e in-data-[variant=card]:*:[tr]:first:*:[td]:last:rounded-se-xl in-data-[variant=card]:*:[tr]:last:*:[td]:first:rounded-es-xl in-data-[variant=card]:*:[tr]:hover:*:[td]:bg-wash-hover in-data-[variant=card]:*:[tr]:data-selected:*:[td]:bg-wash-selected",
        className,
      )}
      data-slot="table-body"
      {...props}
    />
  );
}

export function TableFooter({
  className,
  ...props
}: React.ComponentProps<"tfoot">): React.ReactElement {
  return (
    <tfoot
      className={cn(
        "border-t in-data-[variant=card]:border-none bg-transparent not-in-data-[variant=card]:bg-muted font-medium [&>tr]:last:border-b-0",
        className,
      )}
      data-slot="table-footer"
      {...props}
    />
  );
}

export type TableRowProps = React.ComponentProps<"tr">;

export function TableRow({
  className,
  onClick,
  onKeyDown,
  tabIndex,
  ...props
}: TableRowProps): React.ReactElement {
  // A row that IS the press target gets the keyboard for free: `<tr>` is not
  // focusable and has no implicit activation, so an `onClick` row takes a tab
  // stop and answers Enter and Space itself. The target guard keeps the
  // controls inside the row their own press targets — a Space on a switch is
  // the switch's, and it never reaches the row.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    onKeyDown?.(event);
    if (!onClick || event.defaultPrevented) return;
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    // The row's own click, so the handler reads a real MouseEvent.
    event.currentTarget.click();
  };

  return (
    <tr
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick && tabIndex === undefined ? 0 : tabIndex}
      // The row wash is a background LAYER over the row's own fill, published as
      // `--row-wash` so anything inside the row can replay the identical layer.
      // A wash held in `background-color` cannot be replayed: a sticky pinned
      // cell has to be opaque (the scrolling columns pass behind it), and
      // `background-color: inherit` hands it the row's translucent wash, which
      // it then composites a SECOND time over the row's own coat — one row, two
      // shades, split exactly at the pinned boundary.
      className={cn(
        "relative border-b [--row-wash:transparent] not-in-data-[variant=card]:bg-linear-to-b not-in-data-[variant=card]:from-(--row-wash) not-in-data-[variant=card]:to-(--row-wash) not-in-data-[variant=card]:hover:[--row-wash:var(--wash-hover)] not-in-data-[variant=card]:data-selected:[--row-wash:var(--wash-selected)]",
        onClick && "cursor-pointer",
        className,
      )}
      data-slot="table-row"
      {...props}
    />
  );
}

// `align` is the deprecated presentational attribute on both native types, and
// `<td>` carries a deprecated `width` as well. The semantic props replace them.
export type TableHeadProps = Omit<React.ComponentProps<"th">, "align"> &
  Omittable<{
    /** The column's px width. It holds under `Table layout="fixed"`. */
    width?: number;
    /** Which edge the content sits against. Give a numeric column `"end"` on
     *  its head and on every cell under it, so the figures read down one
     *  edge. */
    align?: TableAlign;
  }>;

export function TableHead({
  className,
  width,
  align = "start",
  style,
  ...props
}: TableHeadProps): React.ReactElement {
  return (
    <th
      className={cn(
        "h-10 whitespace-nowrap px-2.5 align-middle font-medium text-muted-foreground leading-none has-[[role=checkbox]]:w-px last:has-[[role=checkbox]]:ps-0 first:has-[[role=checkbox]]:pe-0",
        ALIGN_CLASS[align],
        className,
      )}
      data-slot="table-head"
      style={widthStyle(width, style)}
      {...props}
    />
  );
}

export type TableCellProps = Omit<React.ComponentProps<"td">, "align" | "width"> &
  Omittable<{
    /** The column's px width. It holds under `Table layout="fixed"`. */
    width?: number;
    /** Which edge the content sits against. Give a numeric column `"end"` on
     *  its head and on every cell under it, so the figures read down one
     *  edge. */
    align?: TableAlign;
  }>;

export function TableCell({
  className,
  width,
  align = "start",
  style,
  ...props
}: TableCellProps): React.ReactElement {
  return (
    <td
      className={cn(
        "whitespace-nowrap bg-clip-padding p-2.5 in-data-[slot=table-footer]:py-3.5 align-middle leading-none in-data-[variant=card]:first:ps-[calc(--spacing(2.5)-1px)] in-data-[variant=card]:last:pe-[calc(--spacing(2.5)-1px)] has-[[role=checkbox]]:w-px last:has-[[role=checkbox]]:ps-0 first:has-[[role=checkbox]]:pe-0",
        ALIGN_CLASS[align],
        className,
      )}
      data-slot="table-cell"
      style={widthStyle(width, style)}
      {...props}
    />
  );
}

export function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">): React.ReactElement {
  return (
    <caption
      className={cn(
        "in-data-[variant=card]:my-4 mt-4 text-muted-foreground text-sm",
        className,
      )}
      data-slot="table-caption"
      {...props}
    />
  );
}
